import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate, formatCurrency } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPENSE_TYPES = ['Travel', 'Food & Accommodation', 'Fuel', 'Conveyance', 'Mobile/Internet', 'Office Supplies', 'Client Entertainment', 'Other'];
const VEHICLE_TYPES = ['Car', 'Bike', 'Public Transport', 'Taxi/Cab', 'Auto', 'Other'];

function TravelExpense({ user, onLogout }) {
  const [expenses, setExpenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  
  const [formData, setFormData] = useState({
    expense_type: 'Travel',
    trip_date: new Date().toISOString().split('T')[0],
    from_location: '',
    to_location: '',
    distance_km: '',
    purpose: '',
    amount: '',
    receipt_url: '',
    vehicle_type: 'Car',
    remarks: ''
  });

  useEffect(() => {
    fetchExpenses();
    fetchDashboardStats();
  }, [filterStatus]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/expenses${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    }
  };

  const calculateDistance = () => {
    const from = formData.from_location.trim();
    const to = formData.to_location.trim();
    
    if (!from || !to) {
      alert('Please enter both From and To locations');
      return;
    }

    alert('Automatic route distance requires a configured maps provider. Please enter the verified distance manually.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (selectedExpense) {
        await axios.put(`${API}/expenses/${selectedExpense.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Expense updated successfully!');
      } else {
        await axios.post(`${API}/expenses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Expense submitted successfully!');
      }
      resetForm();
      fetchExpenses();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to save expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApprove = async (expenseId) => {
    if (!window.confirm('Approve this expense?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expenses/${expenseId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Expense approved successfully!');
      fetchExpenses();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to approve expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReject = async (expenseId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expenses/${expenseId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Expense rejected!');
      fetchExpenses();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to reject expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleMarkPaid = async (expenseId) => {
    const paymentDate = prompt('Enter payment date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!paymentDate) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expenses/${expenseId}/mark-paid?payment_date=${paymentDate}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Expense marked as paid!');
      fetchExpenses();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to mark as paid: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setFormData({ ...expense });
    setShowForm(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Expense deleted successfully!');
      fetchExpenses();
      fetchDashboardStats();
    } catch (error) {
      alert('Failed to delete expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      expense_type: 'Travel',
      trip_date: new Date().toISOString().split('T')[0],
      from_location: '',
      to_location: '',
      distance_km: '',
      purpose: '',
      amount: '',
      receipt_url: '',
      vehicle_type: 'Car',
      remarks: ''
    });
    setSelectedExpense(null);
    setShowForm(false);
  };

  const openAddForm = () => {
    setFormData({
      expense_type: 'Travel',
      trip_date: new Date().toISOString().split('T')[0],
      from_location: '',
      to_location: '',
      distance_km: '',
      purpose: '',
      amount: '',
      receipt_url: '',
      vehicle_type: 'Car',
      remarks: ''
    });
    setSelectedExpense(null);
    setShowForm(true);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Travel & Expense</h1>
          <button
            onClick={openAddForm}
            className="px-6 py-3 rounded-lg text-white font-medium"
            style={{backgroundColor: '#2b3380'}}
          >
            + Submit Expense
          </button>
        </div>

        {/* Travel & Expense Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Travel & Expense Overview"
            icon="🚗"
            bgColor="#ff5722"
            stats={[
              {
                label: 'Pending Expenses',
                value: dashboardStats.pending_expenses || 0,
                subtitle: 'Awaiting approval',
                color: '#ff9800'
              },
              {
                label: 'Approved Expenses',
                value: dashboardStats.approved_expenses || 0,
                subtitle: 'To be paid',
                color: '#4caf50'
              },
              {
                label: 'Total Amount',
                value: `₹${(dashboardStats.total_expense_amount || 0).toLocaleString()}`,
                subtitle: 'Approved/Paid',
                color: '#9c27b0'
              },
              {
                label: 'My Claims',
                value: expenses.length || 0,
                subtitle: 'All status',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {/* Expense Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedExpense ? 'Edit Expense' : 'Submit Expense Claim'}
                  </h2>
                  <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Expense Type *</label>
                      <select
                        value={formData.expense_type}
                        onChange={(e) => setFormData({...formData, expense_type: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      >
                        {EXPENSE_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Trip Date *</label>
                      <input
                        type="date"
                        value={formData.trip_date}
                        onChange={(e) => setFormData({...formData, trip_date: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                  </div>

                  {/* Travel Distance Calculator */}
                  <div className="bg-[#2b338015] border border-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <span>🗺️</span> Travel Distance Calculator
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">From Location</label>
                        <input
                          type="text"
                          value={formData.from_location}
                          onChange={(e) => setFormData({...formData, from_location: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="e.g., Mumbai Office"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">To Location</label>
                        <input
                          type="text"
                          value={formData.to_location}
                          onChange={(e) => setFormData({...formData, to_location: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="e.g., Client Office"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Distance (KM)</label>
                        <input
                          type="number"
                          value={formData.distance_km}
                          onChange={(e) => setFormData({...formData, distance_km: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          step="0.1"
                          placeholder="Auto-calculated"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={calculateDistance}
                          className="w-full px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
                        >
                          📍 Calculate Distance
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Vehicle Type</label>
                      <select
                        value={formData.vehicle_type}
                        onChange={(e) => setFormData({...formData, vehicle_type: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {VEHICLE_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Purpose/Description *</label>
                    <textarea
                      value={formData.purpose}
                      onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="2"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Receipt URL (Optional)</label>
                    <input
                      type="text"
                      value={formData.receipt_url}
                      onChange={(e) => setFormData({...formData, receipt_url: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://example.com/receipt.jpg"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Remarks</label>
                    <textarea
                      value={formData.remarks}
                      onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="2"
                    />
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      {selectedExpense ? 'Update' : 'Submit Claim'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <label className="block text-sm font-medium mb-2">Filter by Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full md:w-1/3 px-3 py-2 border rounded-lg"
          >
            <option value="">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Paid">Paid</option>
          </select>
        </div>

        {/* Expenses List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Expense Claims</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From → To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distance</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">{formatDate(expense.trip_date)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">{expense.expense_type}</td>
                    <td className="px-4 py-4 text-sm">
                      {expense.from_location && expense.to_location ? (
                        <div>{expense.from_location} → {expense.to_location}</div>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {expense.distance_km ? `${expense.distance_km} km` : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      ₹{expense.amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        expense.status === 'Approved' ? 'bg-green-100 text-green-800' :
                        expense.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                        expense.status === 'Paid' ? 'bg-[#2b338030] text-[#2b3380]' :
                        expense.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {expense.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {expense.status === 'Draft' && (
                          <>
                            <button
                              onClick={() => handleEdit(expense)}
                              className="text-[#2b3380] hover:text-blue-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(expense.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {(user.role === 'Admin' || user.role === 'HR Manager') && expense.status === 'Pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(expense.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(expense.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {(user.role === 'Admin' || user.role === 'HR Manager') && expense.status === 'Approved' && (
                          <button
                            onClick={() => handleMarkPaid(expense.id)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Mark Paid
                          </button>
                        )}
                        {expense.receipt_url && (
                          <a
                            href={expense.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#2b3380] hover:text-blue-900"
                          >
                            Receipt
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {expenses.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🚗</div>
            <p className="text-gray-600">No expense claims found</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TravelExpense;
